import { Entity } from '@api/api/entity-schema';
import { formatTicker } from '@api/mappers/formatTicker';
import { FlagIcon } from '@design/components/flag-icon/FlagIcon';
import { RegionIcon } from '@design/components/region-icon/RegionIcon';
import { ColorScheme } from '@design/design-tokens/themes/theme-names';
import {
  NonLinearStepper,
  NonLinearStepType,
} from '@terminal/components/non-linear-stepper/NonLinearStepper';
import { NonLinearStepperRevamp } from '@terminal/components/non-linear-stepper/NonLinearStepperRevamp';
import { useThemeStore } from '@terminal/components/theme-controller/stores/useThemeStore';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useTranslation } from 'react-i18next';

import styles from './GeographicStepperSection.module.scss';

interface GeographicStepperSectionProps {
  entity: Entity;
  targetName: string;
  targetType: 'country' | 'region';
  title?: string;
  description?: string;
  activeIndex: number;
}

export const GeographicStepperSection = ({
  entity,
  targetName,
  targetType,
  title,
  activeIndex,
  description,
}: GeographicStepperSectionProps) => {
  const { t } = useTranslation(['widget', 'countries']);
  const isConnectionsDrawersOverhaul = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-5359-connections-drawers-overhaul')
  );
  const isDark = useThemeStore(state => state.colorScheme === ColorScheme.Dark);
  const logoUrl = isDark
    ? entity.logo_urls.light_png
    : entity.logo_urls.dark_png;

  const logoSize = isConnectionsDrawersOverhaul ? 32 : 23;
  const entityName = entity.name_short || entity.name;
  const targetLabel =
    targetType === 'country'
      ? t(`countries:${targetName.toUpperCase()}`)
      : targetName;
  const steps: NonLinearStepType[] = [
    {
      logoUrl,
      hoverLabel: formatTicker(entity),
      title: isConnectionsDrawersOverhaul ? entityName : undefined,
      connectorLabel: t(
        isConnectionsDrawersOverhaul
          ? 'widget:assetOverview.knowledgeGraph.operatesIn'
          : 'widget:assetOverview.knowledgeGraph.exposedTo'
      ),
      isSimple: false,
    },
    {
      imgNode:
        targetType === 'country' ? (
          <FlagIcon
            countryCode={targetName}
            variant="circular"
            customSize={logoSize}
          />
        ) : (
          <RegionIcon region={targetName} size={logoSize} />
        ),
      hoverLabel: targetLabel,
      title: isConnectionsDrawersOverhaul ? targetLabel : undefined,
      iconColor: 'var(--kg-theme)',
    },
  ];

  return (
    <div className={styles.sectionRow} data-testid="geographic-stepper-section">
      {!isConnectionsDrawersOverhaul && title && (
        <div className={styles.titleMedium}>{title}</div>
      )}
      {isConnectionsDrawersOverhaul ? (
        <NonLinearStepperRevamp
          steps={steps}
          activeIndex={activeIndex}
          iconSize={logoSize}
          graphNode={{ id: entity.tag, type: 'company' }}
        />
      ) : (
        <div className={styles.stepperWrapper}>
          <NonLinearStepper
            steps={steps}
            activeIndex={activeIndex}
            iconSize={logoSize}
          />
        </div>
      )}
      {!isConnectionsDrawersOverhaul && description && (
        <div className={styles.bodySmall}>{description}</div>
      )}
    </div>
  );
};
